import { useEffect, useState } from "react";
import { Link } from "react-router";
import { settingsApi } from "@/core/billing/api/settings-api";
import { BillingPageShell } from "@/ui/BillingPageShell";
import { routes } from "@/router/all_routes";

type BilledByForm = {
  name: string;
  address: string;
  phone: string;
  email: string;
  taxId: string;
};

type BankForm = {
  bankName: string;
  accountNumber: string;
  ifsc: string;
};

function readObject<T extends Record<string, string>>(value: unknown, defaults: T): T {
  if (!value || typeof value !== "object" || Array.isArray(value)) return defaults;
  const obj = value as Record<string, unknown>;
  const out = { ...defaults };
  for (const key of Object.keys(defaults) as (keyof T)[]) {
    const v = obj[key as string];
    if (typeof v === "string") out[key] = v as T[keyof T];
  }
  return out;
}

export function SettingsPage() {
  const [billedBy, setBilledBy] = useState<BilledByForm>({
    name: "",
    address: "",
    phone: "",
    email: "",
    taxId: "",
  });
  const [bank, setBank] = useState<BankForm>({ bankName: "", accountNumber: "", ifsc: "" });
  const [termsConditions, setTermsConditions] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    settingsApi
      .get()
      .then((res) => {
        setBilledBy(
          readObject(res.settings["billing.billed_by"], {
            name: "",
            address: "",
            phone: "",
            email: "",
            taxId: "",
          }),
        );
        setBank(
          readObject(res.settings["billing.bank_details"], {
            bankName: "",
            accountNumber: "",
            ifsc: "",
          }),
        );
        const terms = res.settings["billing.terms_conditions"];
        setTermsConditions(
          typeof terms === "string"
            ? terms
            : terms &&
                typeof terms === "object" &&
                "text" in terms &&
                typeof (terms as { text: unknown }).text === "string"
              ? (terms as { text: string }).text
              : "",
        );
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Erreur"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await settingsApi.upsert("billing.billed_by", billedBy);
      await settingsApi.upsert("billing.bank_details", bank);
      await settingsApi.upsert("billing.terms_conditions", { text: termsConditions });
      setSuccess("Paramètres enregistrés");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erreur enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <BillingPageShell
      title="Paramètres Billing"
      subtitle="Billing From, coordonnées bancaires et conditions par défaut sur les factures/devis."
      actions={
        <Link to="/admin-dashboard" className="btn btn-outline-white btn-sm">
          Retour dashboard
        </Link>
      }
    >
      {error && <div className="alert alert-warning">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}
      {loading ? (
        <p className="text-muted">Chargement…</p>
      ) : (
        <form onSubmit={handleSave}>
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-3">Billing From (billed_by)</h6>
              <div className="row gx-3">
                <div className="col-md-6">
                  <label className="form-label">Nom / Raison sociale</label>
                  <input
                    className="form-control"
                    value={billedBy.name}
                    onChange={(e) => setBilledBy({ ...billedBy, name: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={billedBy.email}
                    onChange={(e) => setBilledBy({ ...billedBy, email: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">Téléphone</label>
                  <input
                    className="form-control"
                    value={billedBy.phone}
                    onChange={(e) => setBilledBy({ ...billedBy, phone: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">N° TVA / Tax ID</label>
                  <input
                    className="form-control"
                    value={billedBy.taxId}
                    onChange={(e) => setBilledBy({ ...billedBy, taxId: e.target.value })}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Adresse</label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={billedBy.address}
                    onChange={(e) => setBilledBy({ ...billedBy, address: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-3">Coordonnées bancaires</h6>
              <div className="row gx-3">
                <div className="col-md-4">
                  <label className="form-label">Banque</label>
                  <input
                    className="form-control"
                    value={bank.bankName}
                    onChange={(e) => setBank({ ...bank, bankName: e.target.value })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">N° compte</label>
                  <input
                    className="form-control"
                    value={bank.accountNumber}
                    onChange={(e) => setBank({ ...bank, accountNumber: e.target.value })}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label">IFSC / SWIFT</label>
                  <input
                    className="form-control"
                    value={bank.ifsc}
                    onChange={(e) => setBank({ ...bank, ifsc: e.target.value })}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-3">Conditions générales</h6>
              <textarea
                className="form-control"
                rows={4}
                value={termsConditions}
                onChange={(e) => setTermsConditions(e.target.value)}
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </form>
      )}
      <p className="text-muted fs-12 mt-3 mb-0">
        Route : <code>{routes.tenantSettings}</code>
      </p>
    </BillingPageShell>
  );
}
